// ── QR rendering ────────────────────────────────────────────────────────────────
// One place that knows how a QR image is produced. Today it's the same zero-dependency
// image-service approach already used for the UPI QR in GroupDetail; swapping in a local
// renderer later only touches this file.
import React, { useCallback, useRef, useState } from 'react';

const QR_ENDPOINT = 'https://api.qrserver.com/v1/create-qr-code/';

export function qrImageUrl(data, size = 200) {
  return `${QR_ENDPOINT}?size=${size}x${size}&margin=8&data=${encodeURIComponent(data)}`;
}

export default function QrCode({ data, size = 200, alt = 'QR code', style }) {
  const [failed, setFailed] = useState(false);
  if (!data) return null;
  if (failed) {
    // Offline or the service is unreachable — still show something actionable.
    return (
      <div style={{ width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', border: '2px dashed var(--border)', borderRadius: 12, padding: 12, fontSize: 12, color: 'var(--text-muted)', wordBreak: 'break-all', ...style }}>
        QR unavailable offline — use the link instead
      </div>
    );
  }
  return (
    <img src={qrImageUrl(data, size)} alt={alt} width={size} height={size}
      onError={() => setFailed(true)}
      style={{ borderRadius: 12, background: '#fff', padding: 8, ...style }} />
  );
}

// ── Printable poster ────────────────────────────────────────────────────────────
// Composes the group's identity and its QR into one PNG an admin can print for a notice
// board. Uses the same canvas technique as the product-photo resizer in GroupDetail.
export function QrPoster({ title, subtitle, footer, data, emoji = '🎭', accent = '#2d6049' }) {
  const canvasRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const download = useCallback(async () => {
    setBusy(true);
    try {
      const W = 800, H = 1130; // ~A4 at 96dpi
      const canvas = canvasRef.current || document.createElement('canvas');
      canvas.width = W; canvas.height = H;
      const ctx = canvas.getContext('2d');

      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, W, H);
      ctx.fillStyle = accent;
      ctx.fillRect(0, 0, W, 180);

      ctx.fillStyle = '#ffffff';
      ctx.font = '600 40px Inter, system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(`${emoji}  Scan to join`, W / 2, 108);

      ctx.fillStyle = '#111827';
      ctx.font = '800 52px Inter, system-ui, sans-serif';
      wrapText(ctx, title || '', W / 2, 290, W - 120, 62);

      if (subtitle) {
        ctx.fillStyle = '#4b5563';
        ctx.font = '400 30px Inter, system-ui, sans-serif';
        wrapText(ctx, subtitle, W / 2, 380, W - 140, 40);
      }

      // The QR itself comes from the image service; crossOrigin lets it into the canvas.
      const img = new Image();
      img.crossOrigin = 'anonymous';
      const loaded = new Promise((res, rej) => { img.onload = res; img.onerror = rej; });
      img.src = qrImageUrl(data, 460);
      await loaded;
      ctx.drawImage(img, (W - 460) / 2, 440, 460, 460);

      ctx.fillStyle = '#6b7280';
      ctx.font = '400 24px Inter, system-ui, sans-serif';
      wrapText(ctx, footer || data, W / 2, 970, W - 100, 34);
      ctx.font = '600 22px Inter, system-ui, sans-serif';
      ctx.fillStyle = accent;
      ctx.fillText('Ledxo Hub', W / 2, 1080);

      const a = document.createElement('a');
      a.href = canvas.toDataURL('image/png');
      a.download = `${(title || 'group').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-join-qr.png`;
      a.click();
    } catch {
      // Most likely the QR image blocked canvas export — fall back to opening it directly.
      window.open(qrImageUrl(data, 460), '_blank', 'noopener,noreferrer');
    } finally {
      setBusy(false);
    }
  }, [title, subtitle, footer, data, emoji, accent]);

  return (
    <>
      <canvas ref={canvasRef} style={{ display: 'none' }} />
      <button type="button" className="btn btn-ghost btn-sm" onClick={download} disabled={busy}>
        {busy ? '⏳ Building…' : '🖼️ Download printable poster'}
      </button>
    </>
  );
}

// Centre-aligned word wrap for canvas text.
function wrapText(ctx, text, cx, y, maxWidth, lineHeight) {
  const words = String(text).split(/\s+/);
  let line = '';
  let cursorY = y;
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (ctx.measureText(candidate).width > maxWidth && line) {
      ctx.fillText(line, cx, cursorY);
      line = word;
      cursorY += lineHeight;
    } else {
      line = candidate;
    }
  }
  if (line) ctx.fillText(line, cx, cursorY);
  return cursorY;
}
